import { useEffect, useState } from "react";
import { Download, KeyRound, Send, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, ts } from "@/lib/api";
import { Panel, Th, Td, btn, field } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { HaviOrders } from "@/components/sap/HaviOrders";
import { HaviDeliveries, asnToast } from "@/components/sap/HaviDeliveries";

const HUB = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blobSave = (data, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(data instanceof Blob ? data : new Blob([data], { type: "text/plain" }));
  a.download = name;
  a.click();
};

export const buildUploadScript = (key, folder) => `# EP EDI Order Hub - HAVI file upload (Windows PowerShell 5.1+)
# Uploads every SAP export in the folder to the Hub (type from the file name: Delivery / ASN = SAP delivery for the 856,
# Forecast / Production / Raw / otherwise FG Inventory), then moves it to Processed (or Error). Schedule with Task Scheduler.

$Hub    = "${HUB}"
$Key    = "${key}"
$Folder = "${folder}"
$Log    = Join-Path $PSScriptRoot "havi-upload.log"

Add-Type -AssemblyName System.Net.Http
[Net.ServicePointManager]::SecurityProtocol = [Net.SecurityProtocolType]::Tls12
function Write-Log($msg) { "$(Get-Date -Format 'dd/MM/yyyy HH:mm:ss') $msg" | Add-Content -Path $Log }
foreach ($d in @("Processed", "Error")) { New-Item -ItemType Directory -Force -Path (Join-Path $Folder $d) | Out-Null }

$client = New-Object System.Net.Http.HttpClient
$client.DefaultRequestHeaders.Add("X-Upload-Key", $Key)
$files = Get-ChildItem -Path $Folder -File | Where-Object { $_.Extension -in ".csv", ".txt", ".xlsx", ".xls" }
if (-not $files) { Write-Log "No files to upload" }
foreach ($f in $files) {
    try {
        $form  = New-Object System.Net.Http.MultipartFormDataContent
        $bytes = New-Object System.Net.Http.ByteArrayContent(,[IO.File]::ReadAllBytes($f.FullName))
        $form.Add($bytes, "file", $f.Name)
        $resp = $client.PostAsync("$Hub/havi/upload", $form).Result
        $body = $resp.Content.ReadAsStringAsync().Result
        if ($resp.IsSuccessStatusCode) {
            Move-Item -Force $f.FullName (Join-Path $Folder "Processed")
            Write-Log "OK    $($f.Name) $body"
        } else {
            Move-Item -Force $f.FullName (Join-Path $Folder "Error")
            Write-Log "ERROR $($f.Name) $($resp.StatusCode) $body"
        }
    } catch { Write-Log "ERROR $($f.Name) $($_.Exception.Message)" }
}
`;

const Settings = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;
  const keys = [["havi_supplier_name", "Supplier name (file name)"], ["havi_location_id", "HAVI supplier number (Location ID)"], ["havi_gln", "GLN"]];
  const save = () => api.put("/settings", Object.fromEntries(keys.map(([k]) => [k, cfg[k] || ""]))).then(() => toast.success("HAVI details saved")).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex flex-wrap items-end gap-3 px-5 py-4" data-testid="havi-settings">
      {keys.map(([k, l]) => <label key={k} className="block"><span className="mb-1 block font-mono text-[10px] uppercase text-slate-500">{l}</span><Input value={cfg[k] || ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className={`w-64 ${field}`} data-testid={`havi-${k}`} /></label>)}
      <Button onClick={save} className={btn.primary} data-testid="havi-settings-save">Save</Button>
    </div>
  );
};

const Files = ({ rows, load }) => {
  const dl = (f) => api.get(`/havi/files/${f.id}/download`, { responseType: "blob" }).then((r) => blobSave(r.data, f.filename));
  const send = (f) => api.post(`/havi/files/${f.id}/send`).then((r) => { toast.success(`${f.filename} sent to HAVI - MDN ${r.data.mdn || "ok"}`); load(); }).catch((e) => { toast.error(errMsg(e)); load(); });
  const del = (f) => window.confirm(`Delete ${f.filename}?`) && api.delete(`/havi/files/${f.id}`).then(load);
  if (!rows.length) return <p className="px-5 py-4 text-xs text-slate-500" data-testid="havi-files-empty">No HAVI files yet. Upload a SAP export above, or let the upload script send them.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-slate-800"><tr><Th>Created</Th><Th>File</Th><Th>Type</Th><Th>Rows</Th><Th>From</Th><Th>Status</Th><Th></Th></tr></thead>
        <tbody className="divide-y divide-slate-800/70">
          {rows.map((f) => (
            <tr key={f.id} data-testid={`havi-file-${f.id}`}>
              <Td className="whitespace-nowrap font-mono text-slate-500">{ts(f.created_at)}</Td>
              <Td className="font-mono">{f.filename}<div className="text-xs text-slate-500">{f.source_file}</div></Td>
              <Td>{{ inventory: "FG inventory (daily)", production: "Production (daily)", forecast: "Forecast (weekly)", raw_material: "Raw material inventory" }[f.kind] || f.kind}</Td><Td>{f.rows}</Td><Td>{f.created_by}</Td>
              <Td><StatusBadge status={f.status === "sent" ? "sent" : f.errors?.length || f.status === "error" ? "error" : "ready"} label={f.errors?.length ? `Rejected - ${f.errors.length} error(s)` : f.status === "review" ? "Ready to review" : f.status} />
                {(f.errors?.length > 0 || f.error) && <div className="mt-1 max-w-md text-xs text-rose-300">{f.error || f.errors.slice(0, 3).join("; ")}</div>}</Td>
              <Td className="whitespace-nowrap text-right">
                <Button size="sm" variant="ghost" className={btn.ghost} onClick={() => dl(f)} data-testid={`havi-download-${f.id}`}><Download className="h-3.5 w-3.5" /></Button>
                {f.status !== "sent" && !f.errors?.length && <Button size="sm" variant="ghost" className={btn.ghost} onClick={() => send(f)} data-testid={`havi-send-${f.id}`}><Send className="mr-1 h-3.5 w-3.5" /> Send to HAVI</Button>}
                {f.status !== "sent" && <Button size="sm" variant="ghost" className={btn.danger} onClick={() => del(f)} data-testid={`havi-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UploadKey = ({ st, load }) => {
  const [key, setKey] = useState("");
  const [folder, setFolder] = useState("\\\\qb-server\\ftp\\EDI-GXS-SAP\\HAVI");
  const create = async () => {
    if (st.active && !window.confirm("Create a new key? The script on your server stops working until replaced.")) return;
    const { data } = await api.post("/havi/upload-key");
    setKey(data.key);
    load();
  };
  return (
    <div className="space-y-2 px-5 py-4 text-sm" data-testid="havi-upload-key">
      <p className="text-slate-400">Auto upload from your file server: a PowerShell script uploads each SAP export saved in the folder below and moves it to Processed. {st.active ? `Key active since ${ts(st.created_at)}, last upload ${st.last_used ? ts(st.last_used) : "never"}.` : "No key yet."}</p>
      <div className="flex flex-wrap items-end gap-2">
        <Input value={folder} onChange={(e) => setFolder(e.target.value)} className={`max-w-md ${field}`} data-testid="havi-upload-folder" />
        <Button onClick={create} className={btn.primary} data-testid="havi-create-key"><KeyRound className="mr-1.5 h-4 w-4" /> {st.active ? "New key and script" : "Create key and script"}</Button>
        {st.active && <Button variant="ghost" className={btn.danger} onClick={() => window.confirm("Revoke the upload key?") && api.delete("/havi/upload-key").then(load)} data-testid="havi-revoke-key">Revoke</Button>}
      </div>
      {key && <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-3"><p className="text-amber-200">Download now - the key is shown once.</p><code className="block break-all text-xs">{key}</code>
        <Button className={`mt-2 ${btn.primary}`} onClick={() => blobSave(buildUploadScript(key, folder).replace(/\n/g, "\r\n"), "havi-upload.ps1")} data-testid="havi-download-script"><Download className="mr-1.5 h-4 w-4" /> Download havi-upload.ps1</Button></div>}
      <p className="text-xs text-slate-500">Schedule: <code className="break-all text-slate-300">schtasks /Create /TN "EP Hub HAVI upload" /TR "powershell.exe -NoProfile -ExecutionPolicy Bypass -File C:\EPHub\havi-upload.ps1" /SC DAILY /ST 06:00 /RU DOMAIN\serviceaccount /RP *</code></p>
    </div>
  );
};

export const HaviPanel = () => {
  const [rows, setRows] = useState([]);
  const [st, setSt] = useState(null);
  const [kind, setKind] = useState("");
  const [tick, setTick] = useState(0);
  const load = () => { api.get("/havi/files").then((r) => setRows(r.data)); api.get("/havi/upload-key").then((r) => setSt(r.data)); setTick((t) => t + 1); };
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", kind);
    try {
      const { data } = await api.post("/havi/upload", fd);
      if (data.asn) { asnToast(data); load(); return; }
      data.errors.length ? toast.warning(`${data.filename}: ${data.rows} rows, ${data.errors.length} error(s)`) : toast.success(`${data.filename} created - ${data.rows} rows, ready to review`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const tpl = (k) => api.get(`/havi/template/${k}`, { responseType: "blob" }).then((r) => blobSave(r.data, `HAVI_${k}_template.csv`));
  return (
    <div className="space-y-4" data-testid="havi-tab">
      <p className="text-sm text-slate-400">McDonald's Assured Supply files for HAVI: FG Inventory and Production (daily), Forecast (weekly) and Raw Material Inventory, pipe-delimited, sent over AS2 to HAVI (OpenText/Liaison). SAP exports already in HAVI's layout (e.g. Inventory20260925.CSV) are checked, renamed to HAVI's naming and kept for review; the type comes from the file name (Forecast / Production / Raw / otherwise Inventory). Click Send to HAVI once checked.</p>
      {st && <Button onClick={() => api.post("/havi/setup").then(() => { toast.success(st.partner ? "HAVI EDI switched on: 850 in, 855 / 856 out, 997" : "HAVI trading partner created with AS2 details"); load(); })} className={st.partner ? btn.ghost : btn.primary} variant={st.partner ? "ghost" : "default"} data-testid="havi-setup-btn">{st.partner ? "Switch on HAVI EDI (850 / 855 / 856 / 997)" : "Create HAVI trading partner (AS2 havi001)"}</Button>}
      <Panel title="HAVI details" testId="havi-details-panel"><Settings /></Panel>
      <Panel title="SAP export to HAVI file" testId="havi-upload-panel">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={`h-9 rounded-md border px-2 text-sm ${field}`} data-testid="havi-kind">
            <option value="">Type from file name</option><option value="inventory">FG inventory (daily)</option><option value="production">Production (daily)</option><option value="forecast">Forecast (weekly)</option><option value="raw_material">Raw material inventory</option><option value="asn">SAP delivery (856 ASN)</option>
          </select>
          <label className={`inline-flex h-9 cursor-pointer items-center rounded-md px-3 text-sm ${btn.ghost}`} data-testid="havi-upload-label"><Upload className="mr-1.5 h-4 w-4" /> Upload SAP export<input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={upload} data-testid="havi-upload-input" /></label>
          <Button variant="ghost" className={btn.ghost} onClick={() => tpl("raw_material")} data-testid="havi-template-inventory">Raw material template</Button>
          <Button variant="ghost" className={btn.ghost} onClick={() => tpl("forecast")} data-testid="havi-template-forecast">Forecast template</Button>
        </div>
        {st && <UploadKey st={st} load={load} />}
      </Panel>
      <Panel title="HAVI files" testId="havi-files-panel"><Files rows={rows} load={load} /></Panel>
      <Panel title="HAVI orders (850 in - SAP IDoc - 855 / 856 out)" testId="havi-orders-panel">{st && <HaviOrders partner={st.partner} key={tick} />}</Panel>
      <Panel title="SAP deliveries to 856 ASN" testId="havi-deliveries-panel"><HaviDeliveries tick={tick} /></Panel>
    </div>
  );
};
